import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { login } from "../../context/AuthContext";
import { HERO_IMAGE } from "../../data/media";

const lineWrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.3 } },
};
const line = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } },
};

const HEADLINE = ["TRAIN LIKE", "YOUR LIFE", "DEPENDS ON IT."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <section ref={ref} id="top" className="relative h-[100svh] min-h-[680px] overflow-hidden grain">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img
          src={HERO_IMAGE}
          alt="Firearms training on the range"
          className="w-full h-full object-cover img-treat"
          style={{ filter: "grayscale(70%) contrast(1.1) brightness(0.55)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/50 to-brand-bg/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-bg/80 via-transparent to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 h-full flex flex-col justify-end pb-16 md:pb-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="h-px w-12 bg-brand-orange" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand-brass">
            Midwest Made · Firearms Instruction
          </span>
        </motion.div>

        <motion.h1
          variants={lineWrap}
          initial="hidden"
          animate="show"
          className="font-heading font-black uppercase leading-[0.86] tracking-tighter text-brand-ink text-5xl sm:text-7xl lg:text-8xl"
          data-testid="hero-headline"
        >
          {HEADLINE.map((l, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={line} className="block">
                {i === 2 ? (
                  <>
                    <span className="text-stroke">DEPENDS</span> ON IT.
                  </>
                ) : (
                  l
                )}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <p className="max-w-md text-brand-muted text-base leading-relaxed">
            Disciplined, safety-first firearms instruction rooted in Midwest values.
            Build real competence — from your first handling to concealed carry and
            defensive carbine — with a coach who takes it as seriously as you do.
          </p>

          <div className="flex items-center gap-4">
            <button
              onClick={login}
              data-testid="hero-book-btn"
              className="group flex items-center gap-3 bg-brand-orange text-brand-bg font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 hover:bg-brand-ink transition-colors"
            >
              Book a Session
              <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </button>
            <a
              href="#courses"
              data-testid="hero-explore-btn"
              className="font-mono text-xs uppercase tracking-[0.2em] border border-white/20 text-brand-ink px-7 py-4 hover:border-brand-orange transition-colors"
            >
              View Training
            </a>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
